/** Colores de fondo de card según diseño Figma / Pulse legacy. */
export const CARD_VARIANTS = [
  "blue",
  "green-dark",
  "green",
  "orange",
  "white",
] as const;

export type CardVariant = (typeof CARD_VARIANTS)[number];

const VARIANT_HEX: Record<CardVariant, string> = {
  blue: "#0AB2FE",
  "green-dark": "#003F28",
  green: "#27BF63",
  orange: "#FFA61C",
  white: "#FFFFFF",
};

const VARIANT_CLASSES: Record<CardVariant, string> = {
  blue: "bg-[#0AB2FE]",
  "green-dark": "bg-[#003F28]",
  green: "bg-[#27BF63]",
  orange: "bg-[#FFA61C]",
  white: "bg-white",
};

const PLUS_CLASSES: Record<CardVariant, string> = {
  blue: "bg-[#003F28] text-white",
  "green-dark": "bg-[#FFA61C] text-[#003F28]",
  green: "bg-white text-[#003F28]",
  orange: "bg-[#003F28] text-white",
  white: "bg-[#0AB2FE] text-[#003F28]",
};

/** Variantes fijas del módulo duo (Figma 1513:21918, 1126:5508). */
const DUO_VARIANTS: CardVariant[] = ["white", "blue"];

/** Variantes fijas featured-next — Figma 1126:5509–5512. */
const FEATURED_NEXT_VARIANTS: CardVariant[] = ["blue", "green-dark", "green", "orange"];

export function variantAt(index: number): CardVariant {
  return CARD_VARIANTS[index % CARD_VARIANTS.length]!;
}

export function featuredNextIndex(slotIndex: number): number {
  return Math.max(0, Math.min(FEATURED_NEXT_VARIANTS.length - 1, slotIndex - 9));
}

export type StoryCardLayout =
  | "featured"
  | "featured-next"
  | "duo"
  | "headline"
  | "layered-bottom";

function layeredBottomSlotIndex(slotIndex: number): number {
  return Math.max(0, Math.min(1, slotIndex - 7));
}

export function variantForSlot(
  layout: StoryCardLayout,
  slotIndex: number,
): CardVariant {
  if (layout === "duo") {
    const duoIndex = Math.max(0, slotIndex - 4);
    return DUO_VARIANTS[duoIndex % DUO_VARIANTS.length]!;
  }
  if (layout === "featured-next") {
    return FEATURED_NEXT_VARIANTS[featuredNextIndex(slotIndex)]!;
  }
  return variantAt(slotIndex);
}

export function variantBgClass(variant: CardVariant): string {
  return VARIANT_CLASSES[variant];
}

/** Hex de fondo de la card de origen (para detalle overlay). */
export function cardSourceBgHex(layout: StoryCardLayout, slotIndex: number): string {
  if (layout === "headline") return "#FF9C1A";
  if (layout === "layered-bottom") {
    return layeredBottomSlotIndex(slotIndex) === 0 ? "#003F28" : "#F8DDC4";
  }
  return VARIANT_HEX[variantForSlot(layout, slotIndex)];
}

export function variantPlusClass(variant: CardVariant): string {
  return PLUS_CLASSES[variant];
}

/** Título en cards destacadas: texto oscuro salvo fondo verde oscuro. */
export function featuredTitleTextClass(variant: CardVariant): string {
  return variant === "green-dark" ? "text-white" : "text-[#003F28]";
}

/** Primera frase del resumen (para duo / layered bottom). */
export function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^[^.!?]+[.!?]?/);
  return (match?.[0] ?? trimmed).trim();
}
